/* Juicebox theme scripts — no dependencies. */
(() => {
  const J = window.Juicebox || { routes: {}, strings: {}, cartType: 'drawer' };

  const fetchJSON = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(async (r) => {
      const data = await r.json();
      if (!r.ok || data.status) throw data;
      return data;
    });

  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => (el.textContent = count));
  };

  /* ---------- Header: scroll state ---------- */
  class HeaderComponent extends HTMLElement {
    connectedCallback() {
      if (this.dataset.sticky !== 'true') return;
      const onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 8);
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      this.querySelectorAll('[data-drawer-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.querySelector('[data-drawer-nav]')?.removeAttribute('open'))
      );
      // Close desktop dropdowns / search when clicking elsewhere
      document.addEventListener('click', (e) => {
        this.querySelectorAll('details[open]').forEach((d) => {
          if (!d.contains(e.target)) d.removeAttribute('open');
        });
      });
      this.querySelector('[data-search]')?.addEventListener('toggle', (e) => {
        if (e.target.open) e.target.querySelector('input[type="search"]')?.focus();
      });
      const nav = this.querySelector('[data-drawer-nav]');
      nav?.addEventListener('toggle', () => document.body.style.overflow = nav.open ? 'hidden' : '');
    }
  }
  customElements.define('header-component', HeaderComponent);

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((btn) =>
        btn.addEventListener('click', () => {
          const min = parseInt(this.input.min || 0, 10);
          const v = parseInt(this.input.value || 0, 10) + (btn.name === 'plus' ? 1 : -1);
          this.input.value = Math.max(min, v);
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        })
      );
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Cart drawer ---------- */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.bind();
      document.querySelectorAll('[data-cart-toggle]').forEach((a) =>
        a.addEventListener('click', (e) => {
          if (J.cartType !== 'drawer') return;
          e.preventDefault();
          this.open();
        })
      );
      document.addEventListener('keydown', (e) => e.key === 'Escape' && this.close());
    }
    bind() {
      this.querySelectorAll('[data-drawer-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.querySelectorAll('[data-cart-remove]').forEach((el) =>
        el.addEventListener('click', (e) => {
          e.preventDefault();
          this.change(el.dataset.cartRemove, 0);
        })
      );
      this.querySelectorAll('[data-cart-qty] input').forEach((input) =>
        input.addEventListener('change', () => this.change(input.dataset.line, input.value))
      );
    }
    open() {
      this.lastFocus = document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.querySelector('.drawer__panel')?.focus();
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.lastFocus?.focus?.();
    }
    render(html) {
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('cart-drawer');
      if (!fresh) return;
      this.innerHTML = fresh.innerHTML;
      this.bind();
    }
    change(line, quantity) {
      this.querySelector(`.cart-line[data-line="${line}"]`)?.classList.add('is-loading');
      const sectionId = this.closest('[id^="shopify-section"]')?.id.replace('shopify-section-', '') || 'cart-drawer';
      fetchJSON(J.routes.cartChange + '.js', { line: Number(line), quantity: Number(quantity), sections: [sectionId] })
        .then((cart) => {
          this.render(cart.sections && cart.sections[sectionId]);
          updateCartCount(cart.item_count);
          this.querySelector('.drawer__panel')?.focus();
        })
        .catch(() => window.location.reload());
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  /* ---------- Add to cart (product page, featured product, quick add) ---------- */
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form || J.cartType !== 'drawer' || !document.querySelector('cart-drawer')) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    const errorEl = form.querySelector('[data-form-error]');
    btn?.classList.add('is-loading');
    btn?.setAttribute('aria-busy', 'true');
    const drawer = document.querySelector('cart-drawer');
    const sectionId = drawer.closest('[id^="shopify-section"]')?.id.replace('shopify-section-', '') || 'cart-drawer';
    const fd = new FormData(form);
    fd.append('sections', sectionId);
    fd.append('sections_url', window.location.pathname);
    fetch(J.routes.cartAdd + '.js', { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: fd })
      .then((r) => r.json())
      .then((res) => {
        if (res.status) throw res;
        if (errorEl) errorEl.hidden = true;
        drawer.render(res.sections && res.sections[sectionId]);
        return fetch(J.routes.cart + '.js').then((r) => r.json());
      })
      .then((cart) => {
        updateCartCount(cart.item_count);
        btn?.classList.add('is-added');
        setTimeout(() => btn?.classList.remove('is-added'), 1600);
        drawer.open();
      })
      .catch((err) => {
        if (errorEl) {
          errorEl.textContent = err.description || err.message || J.strings.error;
          errorEl.hidden = false;
        }
      })
      .finally(() => {
        btn?.classList.remove('is-loading');
        btn?.removeAttribute('aria-busy');
      });
  });

  /* ---------- Cart page quantity ---------- */
  document.querySelectorAll('#CartPageForm [data-cart-qty] input').forEach((input) =>
    input.addEventListener('change', () => {
      input.closest('.cart-line')?.classList.add('is-loading');
      fetchJSON(J.routes.cartChange + '.js', { line: Number(input.dataset.line), quantity: Number(input.value) })
        .finally(() => window.location.reload());
    })
  );
  document.querySelectorAll('#CartPageForm [data-cart-remove]').forEach((a) =>
    a.addEventListener('click', (e) => {
      e.preventDefault();
      fetchJSON(J.routes.cartChange + '.js', { line: Number(a.dataset.cartRemove), quantity: 0 }).finally(() => window.location.reload());
    })
  );

  /* ---------- Variant picker ---------- */
  const formatMoney = (cents) => {
    const fmt = J.moneyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const withCommas = (v, t = ',', d = '.') => {
      const [i, f] = v.split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (f ? d + f : '');
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key === 'amount_no_decimals') return withCommas(Math.round(cents / 100).toString());
      if (key === 'amount_with_comma_separator') return withCommas(value, '.', ',');
      if (key === 'amount_no_decimals_with_comma_separator') return withCommas(Math.round(cents / 100).toString(), '.');
      return withCommas(value);
    });
  };

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
      this.root = this.closest('product-info');
      this.addEventListener('change', () => this.update());
      this.updateAvailability();
    }
    selectedOptions() {
      return [...this.querySelectorAll('fieldset')].map((fs) => fs.querySelector('input:checked')?.value);
    }
    update() {
      const opts = this.selectedOptions();
      this.querySelectorAll('fieldset').forEach((fs, i) => {
        const label = fs.querySelector('[data-selected-value]');
        if (label) label.textContent = opts[i];
      });
      const variant = this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      const idInput = this.root.querySelector('[data-variant-id]');
      const btn = this.root.querySelector('[data-add-button]');
      const label = this.root.querySelector('[data-add-label]');
      this.updateAvailability();
      if (!variant) {
        if (btn) btn.disabled = true;
        if (label) label.textContent = J.strings.unavailable;
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (btn) btn.disabled = !variant.available;
      if (label) label.textContent = variant.available ? J.strings.addToCart : J.strings.soldOut;

      const priceWrap = this.root.querySelector('[data-price] .price');
      if (priceWrap) {
        const onSale = variant.compare_at_price > variant.price;
        priceWrap.classList.toggle('price--on-sale', onSale);
        priceWrap.querySelector('.price__current').lastChild.textContent = formatMoney(variant.price);
        let compare = priceWrap.querySelector('.price__compare');
        if (onSale) {
          if (!compare) {
            compare = document.createElement('s');
            compare.className = 'price__compare';
            priceWrap.appendChild(compare);
          }
          compare.textContent = formatMoney(variant.compare_at_price);
        } else compare?.remove();
      }

      if (variant.featured_media) this.root.dispatchEvent(new CustomEvent('media:select', { detail: variant.featured_media.id }));
      if (this.root.dataset.url && !this.closest('.featured-product')) {
        window.history.replaceState({}, '', `${this.root.dataset.url}?variant=${variant.id}`);
      }
    }
    updateAvailability() {
      const opts = this.selectedOptions();
      this.querySelectorAll('fieldset').forEach((fs, idx) => {
        fs.querySelectorAll('input').forEach((input) => {
          const test = [...opts];
          test[idx] = input.value;
          const ok = this.variants.some((v) => v.available && v.options.every((o, i) => o === test[i]));
          input.nextElementSibling?.classList.toggle('is-unavailable', !ok);
        });
      });
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------- Product media (mobile dots + variant image) ---------- */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const list = this.querySelector('[data-media-list]');
      const dots = [...this.querySelectorAll('.product__dot')];
      if (list && dots.length) {
        list.addEventListener('scroll', () => {
          const i = Math.round(list.scrollLeft / list.clientWidth);
          dots.forEach((d, j) => d.classList.toggle('is-active', i === j));
        }, { passive: true });
      }
      this.addEventListener('media:select', (e) => {
        const item = this.querySelector(`[data-media-id="${e.detail}"]`);
        if (!item || !list) return;
        if (list.scrollWidth > list.clientWidth) list.scrollTo({ left: item.offsetLeft - list.offsetLeft, behavior: 'smooth' });
        else if (list.firstElementChild !== item && list.classList.contains('product__media--gallery')) list.prepend(item);
      });
    }
  }
  customElements.define('product-info', ProductInfo);

  /* ---------- Recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.children.length) return;
      const io = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        fetch(this.dataset.url)
          .then((r) => r.text())
          .then((html) => {
            const el = new DOMParser().parseFromString(html, 'text/html').querySelector('product-recommendations');
            if (el && el.innerHTML.trim()) this.innerHTML = el.innerHTML;
          });
      }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Countdown ---------- */
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      const raw = (this.dataset.end || '').trim().replace(' ', 'T');
      this.end = new Date(raw).getTime();
      if (isNaN(this.end)) return;
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      const diff = Math.max(0, this.end - Date.now());
      const pad = (n) => String(n).padStart(2, '0');
      const s = Math.floor(diff / 1000);
      const set = (k, v) => { const el = this.querySelector(`[data-${k}]`); if (el) el.textContent = pad(v); };
      set('days', Math.floor(s / 86400));
      set('hours', Math.floor((s % 86400) / 3600));
      set('minutes', Math.floor((s % 3600) / 60));
      set('seconds', s % 60);
      if (diff === 0) clearInterval(this.timer);
    }
  }
  customElements.define('countdown-timer', CountdownTimer);

  /* ---------- Collection filters: submit on change ---------- */
  class FacetForm extends HTMLElement {
    connectedCallback() {
      const form = this.querySelector('form');
      let t;
      this.querySelectorAll('[data-facet-input]').forEach((input) =>
        input.addEventListener('change', () => {
          clearTimeout(t);
          t = setTimeout(() => {
            const params = new URLSearchParams(new FormData(form));
            for (const [k, v] of [...params.entries()]) if (v === '') params.delete(k);
            window.location.search = params.toString();
          }, input.type === 'number' ? 600 : 0);
        })
      );
    }
  }
  customElements.define('facet-form', FacetForm);

  /* ---------- Fit giant wordmark to its container ---------- */
  const fitText = () => {
    document.querySelectorAll('[data-fit-text]').forEach((el) => {
      el.style.fontSize = '';
      const base = parseFloat(getComputedStyle(el).fontSize);
      const ratio = el.clientWidth / el.scrollWidth;
      el.style.fontSize = `${Math.floor(base * Math.min(ratio * 0.98, 1.6))}px`;
    });
  };
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitText); else fitText();
  window.addEventListener('resize', fitText);

  /* ---------- Customer: recover password toggle, address delete ---------- */
  const toggleRecover = (show) => {
    const r = document.getElementById('recover');
    const l = document.getElementById('login');
    if (!r || !l) return;
    r.hidden = !show;
    l.hidden = show;
  };
  document.querySelectorAll('[data-toggle-recover]').forEach((a) =>
    a.addEventListener('click', (e) => {
      e.preventDefault();
      toggleRecover(a.getAttribute('href') === '#recover');
    })
  );
  if (window.location.hash === '#recover') toggleRecover(true);
  document.querySelectorAll('[data-confirm]').forEach((btn) =>
    btn.addEventListener('click', (e) => {
      if (!window.confirm(btn.dataset.confirm)) e.preventDefault();
    })
  );
})();
